import type { Mention, MentionMediaResponse } from '@fluenceur/types';
import { useCallback, useMemo, useState } from 'react';
import { useMentionMediaQuery } from '@/hooks/queries/use-mention-media-query';

/** Load failures after which a thumbnail stops trying: the stored address, then one refreshed. */
const MAX_LOAD_FAILURES = 2;

interface UseMentionThumbnailMediaOptions {
  mention: Mention;
  enabled?: boolean;
}

interface MentionThumbnailMedia {
  media: MentionMediaResponse | undefined;
  isLoading: boolean;
  error: Error | null;
  /** The refreshed address failed too: show the placeholder, ask nothing more. */
  isUnavailable: boolean;
  /** Call when the image or video fails to load. */
  onMediaError: () => void;
}

/**
 * Where a grid thumbnail gets its media from. The list already carries what the
 * stored columns hold, so a card draws from it with no request of its own; the
 * media endpoint is asked only when the list says the address needs a refresh
 * (or carries no media), and once, with a forced refresh, when the address
 * fails to load. A second failure gives up rather than retrying for ever.
 */
export const useMentionThumbnailMedia = ({
  mention,
  enabled = true,
}: UseMentionThumbnailMediaOptions): MentionThumbnailMedia => {
  const [loadFailures, setLoadFailures] = useState(0);
  const stored = mention.media;
  const readsStored = loadFailures === 0 && !!stored && !stored.needsRefresh;
  const isUnavailable = loadFailures >= MAX_LOAD_FAILURES;

  const query = useMentionMediaQuery({
    mentionId: mention.id,
    enabled: enabled && !readsStored && !isUnavailable,
    refreshToken: loadFailures > 0 ? 1 : 0,
  });

  const storedMedia = useMemo<MentionMediaResponse | undefined>(
    () =>
      stored
        ? {
            id: mention.id,
            mediaUrl: stored.mediaUrl,
            mediaType: stored.mediaType,
            isExpired: stored.isExpired,
            hostedThumbnailUrl: stored.hostedThumbnailUrl,
            coverUrl: stored.coverUrl ?? null,
            platform: mention.platform,
          }
        : undefined,
    [stored, mention.id, mention.platform]
  );

  const onMediaError = useCallback(() => {
    setLoadFailures((failures) => Math.min(failures + 1, MAX_LOAD_FAILURES));
  }, []);

  if (readsStored) {
    return { media: storedMedia, isLoading: false, error: null, isUnavailable, onMediaError };
  }

  return {
    media: isUnavailable ? undefined : query.data,
    isLoading: !isUnavailable && query.isLoading,
    error: query.error,
    isUnavailable,
    onMediaError,
  };
};
